import React, { useContext, useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  CheckCircle2,
  UserPlus,
  Mail,
  Phone,
  Lock,
  IdCard,
  Calendar,
  MapPin,
  Home,
  Building2,
  ChevronRight,
} from "lucide-react";
import { GlobalContext } from "../../Context/GlobalContext"; // ← named import, correct path/case
import "./CreateSalesPerson.css"; // aoc-csp-* namespaced CSS
import LoadingScreen from "../Utils/LoadingScreen";

const INIT = {
  productId: "",
  mobile: "",
  password: "",
  email: "",
  name: "",
  aadharNo: "",
  panNo: "",
  dob: "",
  gender: "",
  region: "",
  homeAddress: "",
  officeAddress: "",
};

export default function CreateSalesPerson() {
  const { api, Products = [] } = useContext(GlobalContext); // ← from your context
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const er = {};
    if (!form.productId) er.productId = "Please select a product";
    if (!form.name?.trim()) er.name = "Name is required";
    if (!/^\d{10}$/.test(form.mobile || ""))
      er.mobile = "Enter 10-digit mobile";
    if (!form.password || form.password.length < 6)
      er.password = "Min 6 characters";
    if (!/^\S+@\S+\.\S+$/.test(form.email || ""))
      er.email = "Enter a valid email";
    if (!/^\d{12}$/.test(form.aadharNo || ""))
      er.aadharNo = "Enter 12-digit Aadhaar";
    if (!/^[A-Z]{5}\d{4}[A-Z]{1}$/.test((form.panNo || "").toUpperCase()))
      er.panNo = "Invalid PAN (e.g., ABCDE1234F)";
    if (!form.dob) er.dob = "Date of birth required";
    if (!form.gender) er.gender = "Select gender";
    if (!form.region?.trim()) er.region = "Region is required";
    if (!form.homeAddress?.trim()) er.homeAddress = "Home address required";
    if (!form.officeAddress?.trim())
      er.officeAddress = "Office address required";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const resetAll = () => {
    setForm(INIT);
    setErrors({});
    setSubmitting(false);
    setDone(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setDone(false);
    try {
      // Use your axios instance with Basic Auth already set in headers
      const { data } = await api.post(`/Admin/v1.0/createSalesPerson`, {
        ...form,
      });

      if ((data?.responseCode ?? "00") === "00") {
        setDone(true);
        toast.success("Sales person created successfully");
        // reset but keep success ribbon visible
        setForm(INIT);
      } else {
        throw new Error(
          data?.responseMessage || "Failed to create sales person"
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.responseMessage ||
        err.message ||
        "Something went wrong";
      setErrors((er) => ({ ...er, _server: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  // Map your Products from context
  const productOptions = useMemo(
    () =>
      (Products || []).map((p) => ({
        value: p.pk_id ?? p.pkId ?? p.id ?? "",
        label: p.product_name ?? p.name ?? "Unknown",
      })),
    [Products]
  );

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="aoc-csp-screen w-full bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow">
      <div className="aoc-csp-container h-full overflow-y-auto mx-auto px-4 py-6 md:py-4">
        <div className="aoc-csp-header flex items-center gap-3 mb-6 md:mb-10 animate-fade-in">
          <div className="aoc-csp-badge flex items-center justify-center h-12 w-12 rounded-2xl bg-blue-100 ">
            <UserPlus className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 tracking-tight">
              Create Sales Person
            </h1>
            <p className="text-sm md:text-base text-gray-500">
              Add a new sales person, assign product & region, and set
              credentials.
            </p>
          </div>
        </div>

        <div className="aoc-csp-card relative rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.06)] p-5 md:p-6 animate-rise">
          {done && (
            <div className="aoc-csp-success flex items-center gap-2 mb-4 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">
                Sales person created successfully.
              </span>
              <button
                onClick={resetAll}
                className="ml-auto inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition"
              >
                Create another <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {errors._server && (
            <div className="aoc-csp-alert mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errors._server}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5"
          >
            <Field
              label="Product"
              error={errors.productId}
              icon={<Building2 className="h-4 w-4 text-gray-400" />}
            >
              <select
                name="productId"
                value={form.productId}
                onChange={handleChange}
                className=".aoc-csp-input-form"
              >
                <option value="">Select product</option>
                {productOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Full Name"
              error={errors.name}
              icon={<UserPlus className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className=".aoc-csp-input-form"
                placeholder="e.g., Nikhil Bhati"
              />
            </Field>

            <Field
              label="Email"
              error={errors.email}
              icon={<Mail className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className=".aoc-csp-input-form"
                placeholder="name@company.com"
              />
            </Field>

            <Field
              label="Mobile"
              error={errors.mobile}
              icon={<Phone className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                className=".aoc-csp-input-form"
                placeholder="10-digit number"
                maxLength={10}
              />
            </Field>

            <Field
              label="Password"
              error={errors.password}
              icon={<Lock className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                className=".aoc-csp-input-form"
                placeholder="Min 6 characters"
              />
            </Field>

            <Field
              label="Aadhaar No."
              error={errors.aadharNo}
              icon={<IdCard className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="aadharNo"
                value={form.aadharNo}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 12);
                  handleChange({ target: { name: "aadharNo", value: v } });
                }}
                className=".aoc-csp-input-form"
                placeholder="12-digit Aadhaar"
                maxLength={12}
              />
            </Field>

            <Field
              label="PAN No."
              error={errors.panNo}
              icon={<IdCard className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="panNo"
                value={form.panNo}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "panNo",
                      value: e.target.value.toUpperCase(),
                    },
                  })
                }
                className=".aoc-csp-input-form"
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </Field>

            <Field
              label="Date of Birth"
              error={errors.dob}
              icon={<Calendar className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
                className=".aoc-csp-input-form"
              />
            </Field>

            <Field
              label="Gender"
              error={errors.gender}
              icon={<UserPlus className="h-4 w-4 text-gray-400" />}
            >
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className=".aoc-csp-input-form"
              >
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </Field>

            <Field
              label="Region"
              error={errors.region}
              icon={<MapPin className="h-4 w-4 text-gray-400" />}
            >
              <input
                name="region"
                value={form.region}
                onChange={handleChange}
                className=".aoc-csp-input-form"
                placeholder="e.g., North / West / South / East"
              />
            </Field>

            <Field
              className="md:col-span-2 xl:col-span-3"
              label="Home Address"
              error={errors.homeAddress}
              icon={<Home className="h-4 w-4 text-gray-400" />}
            >
              <textarea
                name="homeAddress"
                value={form.homeAddress}
                onChange={handleChange}
                className=".aoc-csp-input-form min-h-[80px]"
                placeholder="House No., Street, City, State, Pincode"
              />
            </Field>

            <Field
              className="md:col-span-2 xl:col-span-3"
              label="Office Address"
              error={errors.officeAddress}
              icon={<Building2 className="h-4 w-4 text-gray-400" />}
            >
              <textarea
                name="officeAddress"
                value={form.officeAddress}
                onChange={handleChange}
                className=".aoc-csp-input-form min-h-[80px]"
                placeholder="Building, Street, City, State, Pincode"
              />
            </Field>

            <div className="md:col-span-2 xl:col-span-3 flex flex-wrap items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className={`aoc-csp-btn aoc-csp-btn-primary ${
                  submitting
                    ? "opacity-75 cursor-not-allowed"
                    : "hover:scale-[1.02]"
                }`}
              >
                {submitting ? "Creating..." : "Create Sales Person"}
              </button>
              <button
                type="button"
                onClick={resetAll}
                disabled={submitting}
                className="aoc-csp-btn aoc-csp-btn-ghost"
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, error, icon, children, className = "" }) {
  return (
    <div className={`aoc-csp-field ${className}`}>
      <label className="aoc-csp-label">{label}</label>
      <div
        className={`aoc-csp-control ${error ? "aoc-csp-control-error" : ""}`}
      >
        {icon ? <span className="aoc-csp-icon">{icon}</span> : null}
        {children}
      </div>
      {error ? <p className="aoc-csp-error">{error}</p> : null}
    </div>
  );
}
